import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  CUSTOMER_DOC_ACCESS_ERRORS,
  type CustomerDocAccessRuleChange,
  type CustomerDocumentationAccess,
  type DocAccessChoice,
  type DocAccessTargetType,
} from "@xop-any-time/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, FileText, Folder as FolderIcon } from "lucide-react";
import { useMemo, useState, type JSX, type ReactNode } from "react";

import { useAuth } from "../../../auth/AuthProvider.js";
import { NoAccessPanel } from "../../../components/ui/no-access-panel.js";
import { ApiError, isPermissionDenied } from "../../../lib/api.js";
import { customerDocAccessKey, getCustomerDocAccess, saveCustomerDocAccess } from "./api.js";
import {
  buildTree,
  currentChoice,
  documentLock,
  effectiveRule,
  folderLock,
  predictedVisibility,
  subtreeDocuments,
  toRuleChanges,
  withChoice,
  type Doc,
  type FolderNode,
  type LooseGroup,
  type Pending,
  type Visibility,
} from "./tree.js";

/**
 * The customer profile's **Documentation** tab (2026-09-30): the reseller
 * chooses which of the documents IT can read this customer sees on the
 * support portal.
 *
 * PORTAL-OWNED, mounted by the ported `CustomersDetailPage` (one marked edit
 * there — see src/crm/README.md). It lives here, outside `src/crm`, because
 * that tree is not typechecked and this one is.
 *
 * Every choice is Default · Share · Hide, per document and per folder (a
 * folder's choice reaches every document under it that has no nearer choice).
 * Nothing is written until Save, and Save is ONE request carrying every
 * change. portal-api decides; the pills here are a preview, and the page
 * re-renders off portal-api's answer.
 */

export const INTRO =
  "Choose which of the documents you can read this customer sees on the support portal. Default follows what XOP publishes for the products they own.";

/** Whether the tab is offered at all: reading needs both keys the BFF gates
 * the read on. Exported for the customer page's tab filter. */
export function useCanSeeCustomerDocumentation(): boolean {
  const { can } = useAuth();
  return can("customers:view") && can("documentation:view");
}

export function CustomerDocumentationTab({ customerId }: { customerId: string }): JSX.Element {
  const { can } = useAuth();
  const canEdit = can("customers:edit");
  const queryClient = useQueryClient();
  const key = customerDocAccessKey(customerId);

  const query = useQuery({ queryKey: key, queryFn: () => getCustomerDocAccess(customerId) });

  const [pending, setPending] = useState<Pending>(() => new Map());
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set());
  const [notice, setNotice] = useState<{ severity: "success" | "error"; text: string } | null>(null);

  const save = useMutation({
    mutationFn: (rules: CustomerDocAccessRuleChange[]) => saveCustomerDocAccess(customerId, rules),
    onSuccess: (next) => {
      // The answer IS the new state — no second read.
      queryClient.setQueryData(key, next);
      setPending(new Map());
      setNotice({ severity: "success", text: "Saved. The customer's support portal follows these choices now." });
    },
    onError: (err: Error) => {
      setNotice({ severity: "error", text: err.message });
      // A refused target means this page's picture is stale (a document
      // unpublished, a folder closed to the company) — ask again, keep the
      // pending choices so nothing the reseller did is thrown away.
      if (err instanceof ApiError && err.code === CUSTOMER_DOC_ACCESS_ERRORS.targetNotVisible) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    },
  });

  const tree = useMemo(() => (query.data ? buildTree(query.data) : null), [query.data]);

  if (query.isPending) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
        <CircularProgress size={28} aria-label="Loading documentation" />
      </Box>
    );
  }

  if (query.isError) {
    const err = query.error;
    if (isPermissionDenied(err)) return <NoAccessPanel permission={err.permission ?? "documentation:view"} />;
    if (err instanceof ApiError && err.code === CUSTOMER_DOC_ACCESS_ERRORS.notOnPortal) {
      return (
        <Alert severity="info" data-testid="doc-access-not-on-portal">
          This customer is not on the support portal yet, so there is nothing to choose. Once XOP connects
          them, the documentation they can read will appear here.
        </Alert>
      );
    }
    return (
      <Alert
        severity="error"
        data-testid="doc-access-load-error"
        action={<Button color="inherit" size="small" onClick={() => void query.refetch()}>Retry</Button>}
      >
        {err.message || "This customer's documentation could not be loaded."}
      </Alert>
    );
  }

  const access = query.data;
  const choose = (type: DocAccessTargetType, id: number, choice: DocAccessChoice): void => {
    setNotice(null);
    setPending((prev) => withChoice(access, prev, type, id, choice));
  };
  const toggle = (k: string): void =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  const ctx: RowContext = {
    access,
    pending,
    canEdit,
    saving: save.isPending,
    collapsed,
    toggle,
    choose,
  };

  return (
    <Card data-testid="doc-access-tab">
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box>
          <Typography component="h3" sx={{ fontWeight: 600, fontSize: "1.05rem" }}>
            Documentation on the support portal
          </Typography>
          <Typography sx={{ color: "text.secondary", mt: 0.5 }} data-testid="doc-access-intro">
            {INTRO}
          </Typography>
        </Box>

        {!canEdit && (
          <Alert severity="info" data-testid="doc-access-read-only">
            You can see what this customer reads, but changing it needs permission to edit customers. Ask an
            owner at your company.
          </Alert>
        )}

        {notice && (
          <Alert severity={notice.severity} data-testid="doc-access-notice" onClose={() => setNotice(null)}>
            {notice.text}
          </Alert>
        )}

        {tree && tree.folders.length === 0 && tree.loose.length === 0 ? (
          <Typography sx={{ color: "text.secondary", py: 3, textAlign: "center" }} data-testid="doc-access-empty">
            Your company can&apos;t read any documentation yet, so there is nothing to share with this customer.
          </Typography>
        ) : (
          <Box role="tree" aria-label="Documentation this customer can be shown">
            {tree?.folders.map((node) => <FolderRow key={node.folder.id} node={node} depth={0} ctx={ctx} />)}
            {tree?.loose.map((group) => <LooseRow key={group.key} group={group} ctx={ctx} />)}
          </Box>
        )}

        {canEdit && pending.size > 0 && (
          <Paper
            elevation={6}
            data-testid="doc-access-pending"
            sx={{
              position: "sticky",
              bottom: 16,
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.5,
              px: 2,
              py: 1.5,
            }}
          >
            <Typography sx={{ fontWeight: 500 }}>
              {pending.size} unsaved change{pending.size === 1 ? "" : "s"}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="text"
                color="inherit"
                disabled={save.isPending}
                onClick={() => {
                  setPending(new Map());
                  setNotice(null);
                }}
              >
                Discard
              </Button>
              <Button
                variant="contained"
                disabled={save.isPending}
                onClick={() => save.mutate(toRuleChanges(pending))}
              >
                {save.isPending ? "Saving…" : "Save"}
              </Button>
            </Box>
          </Paper>
        )}
      </CardContent>
    </Card>
  );
}

// ── Rows ──────────────────────────────────────────────────────────────────

interface RowContext {
  access: CustomerDocumentationAccess;
  pending: Pending;
  canEdit: boolean;
  saving: boolean;
  collapsed: ReadonlySet<string>;
  toggle: (key: string) => void;
  choose: (type: DocAccessTargetType, id: number, choice: DocAccessChoice) => void;
}

const INDENT = 3; // theme spacing units per level

function FolderRow({ node, depth, ctx }: { node: FolderNode; depth: number; ctx: RowContext }): JSX.Element {
  const k = `folder-${node.folder.id}`;
  const open = !ctx.collapsed.has(k);
  const docs = subtreeDocuments(node);
  const visible = docs.filter((d) => predictedVisibility(ctx.access, ctx.pending, d) === "visible").length;
  const lock = folderLock(node);
  const choice = currentChoice(ctx.access, ctx.pending, "DocumentCategory", node.folder.id);

  return (
    <Box role="treeitem" aria-expanded={open} data-testid={`doc-access-folder-${node.folder.id}`}>
      <RowShell depth={depth} header>
        <IconButton
          size="small"
          onClick={() => ctx.toggle(k)}
          aria-label={`${open ? "Collapse" : "Expand"} ${node.folder.name}`}
        >
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </IconButton>
        <Box sx={{ color: "text.secondary", display: "flex" }}><FolderIcon size={16} /></Box>
        <Box sx={{ flex: 1, minWidth: 160 }}>
          <Typography sx={{ fontWeight: 600 }}>{node.folder.name}</Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {visible} of {docs.length} visible to the customer
          </Typography>
        </Box>
        <RowControl
          label={`Access for folder ${node.folder.name}`}
          choice={choice}
          lock={lock}
          ctx={ctx}
          onChange={(c) => ctx.choose("DocumentCategory", node.folder.id, c)}
        />
      </RowShell>
      {open && (
        <Box role="group">
          {node.folders.map((child) => <FolderRow key={child.folder.id} node={child} depth={depth + 1} ctx={ctx} />)}
          {node.documents.map((doc) => <DocumentRow key={doc.id} doc={doc} depth={depth + 1} ctx={ctx} />)}
        </Box>
      )}
    </Box>
  );
}

function LooseRow({ group, ctx }: { group: LooseGroup; ctx: RowContext }): JSX.Element {
  const k = `loose-${group.key}`;
  const open = !ctx.collapsed.has(k);
  return (
    <Box role="treeitem" aria-expanded={open} data-testid={`doc-access-loose-${group.key}`}>
      <RowShell depth={0} header>
        <IconButton size="small" onClick={() => ctx.toggle(k)} aria-label={`${open ? "Collapse" : "Expand"} ${group.label}`}>
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </IconButton>
        <Box sx={{ color: "text.secondary", display: "flex" }}><FolderIcon size={16} /></Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 600 }}>{group.label}</Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Not filed in a folder — choose per document
          </Typography>
        </Box>
      </RowShell>
      {open && (
        <Box role="group">
          {group.documents.map((doc) => <DocumentRow key={doc.id} doc={doc} depth={1} ctx={ctx} />)}
        </Box>
      )}
    </Box>
  );
}

function DocumentRow({ doc, depth, ctx }: { doc: Doc; depth: number; ctx: RowContext }): JSX.Element {
  const visibility = predictedVisibility(ctx.access, ctx.pending, doc);
  const rule = effectiveRule(ctx.access, ctx.pending, doc);
  const lock = documentLock(doc);
  const choice = currentChoice(ctx.access, ctx.pending, "Document", doc.id);

  return (
    <Box role="treeitem" data-testid={`doc-access-doc-${doc.id}`}>
      <RowShell depth={depth}>
        <Box sx={{ width: 34, display: "flex", justifyContent: "center", color: "text.secondary" }}>
          <FileText size={16} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 160 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography>{doc.title}</Typography>
            {doc.releaseVersion && <Chip size="small" variant="outlined" label={`v${doc.releaseVersion}`} />}
            <VisibilityPill visibility={visibility} testId={`doc-access-pill-${doc.id}`} />
          </Box>
          {!lock && rule?.from === "folder" && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {rule.effect === "allow" ? "Shared" : "Hidden"} by the folder “{rule.folder.name}”
            </Typography>
          )}
        </Box>
        <RowControl
          label={`Access for ${doc.title}`}
          choice={choice}
          lock={lock}
          ctx={ctx}
          onChange={(c) => ctx.choose("Document", doc.id, c)}
        />
      </RowShell>
    </Box>
  );
}

function RowShell({ depth, header = false, children }: { depth: number; header?: boolean; children: ReactNode }): JSX.Element {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1,
        py: 1,
        pl: depth * INDENT,
        borderBottom: 1,
        borderColor: "divider",
        ...(header ? { bgcolor: "action.hover" } : {}),
      }}
    >
      {children}
    </Box>
  );
}

const PILL: Record<Visibility, { label: string; color: "success" | "default" | "info" }> = {
  visible: { label: "Customer sees it", color: "success" },
  hidden: { label: "Hidden from customer", color: "default" },
  default: { label: "Follows XOP's default", color: "info" },
};

function VisibilityPill({ visibility, testId }: { visibility: Visibility; testId: string }): JSX.Element {
  const pill = PILL[visibility];
  return <Chip size="small" variant="outlined" color={pill.color} label={pill.label} data-testid={testId} />;
}

/**
 * Default · Share · Hide — or, read-only, only what is stored.
 *
 * A locked row keeps its control (disabled) with the reason under it, so the
 * reseller can see WHY it cannot change it rather than wondering where the
 * control went.
 */
function RowControl({
  label,
  choice,
  lock,
  ctx,
  onChange,
}: {
  label: string;
  choice: DocAccessChoice;
  lock: string | null;
  ctx: RowContext;
  onChange: (choice: DocAccessChoice) => void;
}): JSX.Element {
  if (!ctx.canEdit) {
    return (
      <Box sx={{ minWidth: 120, textAlign: "right" }}>
        {lock ? (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>{lock}</Typography>
        ) : choice ? (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {choice === "allow" ? "Shared by your company" : "Hidden by your company"}
          </Typography>
        ) : null}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.25 }}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={choice ?? "default"}
        disabled={lock !== null || ctx.saving}
        aria-label={label}
        onChange={(_e, next: string | null) => {
          // MUI answers null when the SELECTED button is pressed again; that
          // is not a choice, so the row keeps what it has.
          if (next === null) return;
          onChange(next === "default" ? null : (next as "allow" | "deny"));
        }}
      >
        <ToggleButton value="default">Default</ToggleButton>
        <ToggleButton value="allow">Share</ToggleButton>
        <ToggleButton value="deny">Hide</ToggleButton>
      </ToggleButtonGroup>
      {lock && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>{lock}</Typography>
      )}
    </Box>
  );
}
